import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { httpClient } from '@shared/api';
import { env } from '@shared/config/env';

/** V2-4 этап 7: экран 14 «Баланс и расход» (бэкенд `cabinet_billing.py`). */

export interface Period {
  from: string;
  to: string;
}

export interface BillSummary {
  balance_rub: string;
  bonus_left_rub: string;
  low_balance_threshold: string;
  notify_low_balance: boolean;
  spend_rub: string;
  spend_answers_rub: string;
  spend_kb_rub: string;
  prev_spend_rub: string;
  answers: number;
  avg_answer_rub: string | null;
  topups_rub: string;
  topups_paid_rub: string;
  topups_bonus_rub: string;
  topups_adjust_rub: string;
}

export interface BillPoint {
  t: string;
  total: string;
  answers: string;
  kb: string;
}

export interface BillSeries {
  step: 'day' | 'hour';
  points: BillPoint[];
  overview: BillPoint[];
}

export interface BillRow {
  name: string;
  answers: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  sum_rub: string;
}

export interface BillOp {
  id: string;
  at: string;
  kind: 'answer' | 'kb' | 'topup' | 'bonus' | 'adjustment';
  title: string;
  who: string;
  details: string;
  amount_rub: string;
  balance_after_rub: string;
}

const p = (r: Period) => ({ date_from: r.from, date_to: r.to });

export function useBillSummary(r: Period) {
  return useQuery<BillSummary, Error>({
    queryKey: ['billing-v2', 'summary', r],
    queryFn: async () => (await httpClient.get('/billing/v2/summary', { params: p(r) })).data,
    placeholderData: keepPreviousData,
  });
}

export function useBillSeries(r: Period, step: 'day' | 'hour') {
  return useQuery<BillSeries, Error>({
    queryKey: ['billing-v2', 'series', r, step],
    queryFn: async () =>
      (await httpClient.get('/billing/v2/series', { params: { ...p(r), step } })).data,
    placeholderData: keepPreviousData,
  });
}

export function useBillBreakdown(r: Period, by: 'model' | 'assistant' | 'channel') {
  return useQuery<BillRow[], Error>({
    queryKey: ['billing-v2', 'breakdown', r, by],
    queryFn: async () =>
      (await httpClient.get('/billing/v2/breakdown', { params: { ...p(r), by } })).data,
    placeholderData: keepPreviousData,
  });
}

export function useBillOps(r: Period, assistantId: string | null, kind: string) {
  return useInfiniteQuery<{ items: BillOp[]; total: number; next_cursor: string | null }, Error>({
    queryKey: ['billing-v2', 'ops', r, assistantId, kind],
    queryFn: async ({ pageParam }) =>
      (
        await httpClient.get('/billing/v2/operations', {
          params: {
            ...p(r),
            assistant_id: assistantId || undefined,
            kind,
            cursor: pageParam || undefined,
            limit: 30,
          },
        })
      ).data,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
  });
}

/** Ссылка на CSV-детализацию за период (cookie авторизации уходят сами). */
export function billExportUrl(r: Period) {
  return `${env.API_BASE_URL.replace(/\/$/, '')}/billing/v2/export.csv?date_from=${r.from}&date_to=${r.to}`;
}
